/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

export interface ColumnCandidate {
  column_name?: string | null;
  name?: string | null;
  type?: string | null;
  is_dttm?: boolean | null;
}

const NAME_PREFERENCES = [
  '创建时间',
  'created_at',
  'create_time',
  'order_date',
  'event_time',
  'dt',
  'date',
];

const TEMPORAL_TYPE_RE = /(time|date|timestamp|datetime|temporal)/i;

const TEMPORAL_NAME_RE = /(time|date|创建|更新|dt$|_at$)/i;

export function columnNameOf(column: ColumnCandidate): string {
  return (column.column_name ?? column.name ?? '').trim();
}

export function isTemporalColumnType(type?: string | null): boolean {
  if (!type) {
    return false;
  }
  return TEMPORAL_TYPE_RE.test(type);
}

export function isTemporalColumn(column: ColumnCandidate): boolean {
  const name = columnNameOf(column);
  return (
    Boolean(column.is_dttm) ||
    isTemporalColumnType(column.type) ||
    TEMPORAL_NAME_RE.test(name)
  );
}

function namePreferenceScore(name: string): number {
  const lower = name.toLowerCase();
  const idx = NAME_PREFERENCES.findIndex(
    pref => lower === pref.toLowerCase() || lower.includes(pref.toLowerCase()),
  );
  return idx >= 0 ? NAME_PREFERENCES.length - idx : 0;
}

/**
 * Pick default main_dttm_col for virtual dataset output (J-07).
 */
export function suggestMainDttmColumn(
  columns: ColumnCandidate[],
): string | undefined {
  const temporal = columns
    .map(column => ({ column, name: columnNameOf(column) }))
    .filter(({ column, name }) => name && isTemporalColumn(column));

  if (!temporal.length) {
    return undefined;
  }

  temporal.sort((a, b) => {
    const scoreDiff = namePreferenceScore(b.name) - namePreferenceScore(a.name);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    if (a.column.is_dttm && !b.column.is_dttm) {
      return -1;
    }
    if (!a.column.is_dttm && b.column.is_dttm) {
      return 1;
    }
    return a.name.localeCompare(b.name);
  });

  return temporal[0].name;
}

export function applyMainDttmToColumns<T extends ColumnCandidate>(
  columns: T[],
  mainDttmCol?: string,
): T[] {
  if (!mainDttmCol) {
    return columns;
  }
  return columns.map(column => {
    const name = columnNameOf(column);
    return {
      ...column,
      is_dttm: name === mainDttmCol,
    };
  });
}
